/**
 * MainPopover — web component for inline info popover panels.
 *
 * Markup contract (all selectors use a single `data-main-popover` attribute):
 *   <main-popover>                              ← host element
 *     <button data-main-popover="trigger" …>    ← toggle button
 *     <div    data-main-popover="panel"   …>    ← dropdown panel
 *   </main-popover>
 *
 * Features:
 *   - Toggle on trigger click
 *   - Close on outside click or Escape key
 *   - Auto-flip up / align-right when panel overflows viewport
 *   - Recalculates position on window resize
 */
class MainPopover extends HTMLElement {
  constructor() {
    super();

    this.trigger = this.querySelector('[data-main-popover="trigger"]');
    this.panel = this.querySelector('[data-main-popover="panel"]');

    if (!this.trigger || !this.panel) return;

    this.trigger.addEventListener('click', this.toggle.bind(this));

    this.onDocumentClick = this.handleDocumentClick.bind(this);
    this.onKeydown = this.handleKeydown.bind(this);
    this.onResize = this.handleResize.bind(this);

    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);
    window.addEventListener('resize', this.onResize);
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  toggle(event) {
    event.stopPropagation();
    this.closeOthers();

    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    if (!this.panel || !this.trigger) return;

    this.panel.hidden = false;
    this.classList.add('is-open');
    this.classList.remove('is-open-up', 'is-align-right');

    const triggerRect = this.trigger.getBoundingClientRect();

    this.panel.style.left = '';
    this.panel.style.right = '';
    this.panel.style.maxWidth = '';
    this.panel.style.transform = '';

    const container = this.closest('.product__info-container') || this.closest('product-info');

    let boundaryLeft = 0;
    let boundaryRight = window.innerWidth;
    let maxAllowedWidth = window.innerWidth - 24; // 12px margins on the edges

    if (container) {
      const containerRect = container.getBoundingClientRect();
      boundaryLeft = containerRect.left;
      boundaryRight = containerRect.right;
      maxAllowedWidth = containerRect.width;
    }

    this.panel.style.maxWidth = `${maxAllowedWidth}px`;
    const actualPanelWidth = this.panel.offsetWidth;
    const hostWidth = this.offsetWidth;
    let L = (hostWidth - actualPanelWidth) / 2;
    const hostRect = this.getBoundingClientRect();
    const panelLeftViewport = hostRect.left + L;
    const panelRightViewport = panelLeftViewport + actualPanelWidth;

    if (panelRightViewport > boundaryRight) {
      const overflowRight = panelRightViewport - boundaryRight;
      L -= overflowRight;
    }

    const panelLeftViewportShifted = hostRect.left + L;
    if (panelLeftViewportShifted < boundaryLeft) {
      const overflowLeft = boundaryLeft - panelLeftViewportShifted;
      L += overflowLeft;
    }

    this.panel.style.left = `${L}px`;
    this.panel.style.transform = 'none';

    const panelRect = this.panel.getBoundingClientRect();
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;

    if (spaceBelow < panelRect.height + 16 && spaceAbove > panelRect.height + 16) {
      this.classList.add('is-open-up');
    }

    this.trigger.setAttribute('aria-expanded', 'true');
  }

  close() {
    if (!this.panel || !this.trigger) return;

    this.classList.remove('is-open', 'is-open-up', 'is-align-right');
    this.panel.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');

    this.panel.style.left = '';
    this.panel.style.right = '';
    this.panel.style.maxWidth = '';
    this.panel.style.transform = '';
  }

  closeOthers() {
    document.querySelectorAll('main-popover.is-open').forEach((el) => {
      if (el !== this) el.close();
    });
  }

  handleDocumentClick(event) {
    if (this.isOpen && !this.contains(event.target)) {
      this.close();
    }
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.isOpen) {
      this.close();
      this.trigger.focus();
    }
  }

  handleResize() {
    if (!this.isOpen) return;
    this.close();
    this.open();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('resize', this.onResize);
  }
}

if (!customElements.get('main-popover')) {
  customElements.define('main-popover', MainPopover);
}
